import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import { listUserPermissions } from 'api/admin/userPermissions/listUserPermissions'
import { getUIPermission } from 'api/admin/userPermissions/permissions'
import {
  ListUserPermissionsRequest,
  ListUserPermissionsResponse,
} from 'api/admin/userPermissions/types'
import { useWhoamiContextValue } from 'components/Whoami/WhoamiProvider'
import { userPermissionsKeys } from 'queries/admin/userPermissions/keys'

type UseUserPermissionsQueryKey = ReturnType<typeof userPermissionsKeys.list>
type UseUserPermissionsQueryPageParam = string | undefined

type UseUserPermissionsQueryOptions = Partial<
  UseInfiniteQueryOptions<
    ListUserPermissionsResponse,
    Error,
    InfiniteData<ListUserPermissionsResponse, UseUserPermissionsQueryPageParam>,
    ListUserPermissionsResponse,
    UseUserPermissionsQueryKey,
    UseUserPermissionsQueryPageParam
  >
>

export function useUserPermissionsQuery(
  request: Omit<ListUserPermissionsRequest, 'cursor' | 'direction'> = {},
  options: UseUserPermissionsQueryOptions = {}
) {
  const { hasPermission } = useWhoamiContextValue()
  const permission = getUIPermission(hasPermission)
  return useInfiniteQuery<
    ListUserPermissionsResponse,
    Error,
    InfiniteData<ListUserPermissionsResponse, UseUserPermissionsQueryPageParam>,
    UseUserPermissionsQueryKey,
    UseUserPermissionsQueryPageParam
  >({
    ...options,
    queryKey: userPermissionsKeys.list(request),
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      listUserPermissions(
        {
          ...request,
          direction: 'next',
          ...(pageParam ? { cursor: pageParam } : {}),
        },
        permission || 'permission_admin'
      ),
    getNextPageParam: (lastPage) => {
      if (lastPage.users.length === 0) {
        return undefined
      }
      return lastPage.nextCursor ?? undefined
    },
  })
}
